import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const UNIT_COST_DECIMAL_PATTERN = /^\d+(\.\d{1,2})?$/;

interface AddCustomLineItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateCustomLineItem: (args: {
    rawValue: string;
    unitCost: number | null;
  }) => Promise<void>;
}

export function AddCustomLineItemDialog({
  open,
  onOpenChange,
  onCreateCustomLineItem,
}: AddCustomLineItemDialogProps) {
  const [name, setName] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error("Custom line item name is required");
      return;
    }

    const trimmedUnitCost = unitCost.trim();
    if (
      trimmedUnitCost !== "" &&
      !UNIT_COST_DECIMAL_PATTERN.test(trimmedUnitCost)
    ) {
      toast.error("Unit cost must be a whole number or have up to 2 decimals");
      return;
    }

    const parsedUnitCost = trimmedUnitCost === "" ? null : Number(trimmedUnitCost);
    if (parsedUnitCost !== null && parsedUnitCost < 0) {
      toast.error("Unit cost must be zero or greater");
      return;
    }

    setIsSaving(true);
    try {
      await onCreateCustomLineItem({
        rawValue: trimmedName,
        unitCost: parsedUnitCost,
      });
      setName("");
      setUnitCost("");
      onOpenChange(false);
    } catch {
      // onCreateCustomLineItem reports the failure.
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!isSaving) {
          onOpenChange(nextOpen);
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Custom Line Item</DialogTitle>
          <DialogDescription>
            Create a persisted tag for manual takeoff measurements and optional cost
            tracking.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <label
              className="text-sm font-medium"
              htmlFor="custom-line-item-name"
            >
              Name
            </label>
            <Input
              id="custom-line-item-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Enter custom line item"
            />
          </div>
          <div className="space-y-2">
            <label
              className="text-sm font-medium"
              htmlFor="custom-line-item-unit-cost"
            >
              Unit Cost
            </label>
            <Input
              id="custom-line-item-unit-cost"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={unitCost}
              onChange={(event) => setUnitCost(event.target.value)}
              placeholder="0.00"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={isSaving}>
            Save Line Item
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
